<div class="w-full h-full flex flex-col p-4 dark:text-app-gray-300">

  <!-- Header -->
  <div class="flex items-start justify-between mb-1 gap-4">
    <div class="flex items-center gap-3">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Services</h2>
      <span class="text-[11px] text-gray-500 dark:text-gray-400">Read-only view of pm2-managed services on this box and peer.</span>
    </div>
    <div class="flex items-center gap-2">
      <!-- Services / Crons tab pair. Same URL, mode toggles state
           in-component (no route change) so refresh returns to the
           default Services tab — matches Mike's expectation. -->
      <div class="flex rounded overflow-hidden border border-gray-300 dark:border-[#434863]">
        <button type="button"
                class="text-[11px] uppercase tracking-wide px-3 py-1 transition"
                [class.bg-app-teal-100]="viewMode === 'services'"
                [class.text-white]="viewMode === 'services'"
                [class.text-gray-700]="viewMode !== 'services'"
                [class.dark:text-gray-300]="viewMode !== 'services'"
                (click)="switchView('services')">
          Services
        </button>
        <button type="button"
                class="text-[11px] uppercase tracking-wide px-3 py-1 transition border-l border-gray-300 dark:border-[#434863]"
                [class.bg-app-teal-100]="viewMode === 'crons'"
                [class.text-white]="viewMode === 'crons'"
                [class.text-gray-700]="viewMode !== 'crons'"
                [class.dark:text-gray-300]="viewMode !== 'crons'"
                (click)="switchView('crons')">
          Crons
        </button>
      </div>
      <button type="button"
              class="text-[11px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-1
                     text-gray-700 dark:text-gray-300 hover:text-app-teal-100 hover:border-app-teal-100 transition"
              (click)="viewMode === 'crons' ? loadCrons() : loadList()">
        <i class="fa-solid fa-rotate mr-1"></i>Refresh
      </button>
    </div>
  </div>

  <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
    Clearing a log truncates the file in place — the owning pm2 process keeps writing, no restart. Env values whose names look secret are masked server-side.
  </p>

  <!-- Box filter -->
  <div class="flex items-center gap-2 mb-3">
    <span class="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mr-2">Box</span>
    <button type="button"
            class="text-xs px-2 py-1 rounded border border-gray-300 dark:border-[#434863]"
            [class.bg-app-teal-100]="selectedBox === null"
            [class.text-white]="selectedBox === null"
            (click)="selectBoxFilter(null)">
      All
    </button>
    <button *ngFor="let b of boxes" type="button"
            class="text-xs px-2 py-1 rounded border border-gray-300 dark:border-[#434863]"
            [class.bg-app-teal-100]="selectedBox === b"
            [class.text-white]="selectedBox === b"
            [title]="boxTitle(b)"
            (click)="selectBoxFilter(b)">
      {{ b }}<span *ngIf="b === localBox" class="ml-1 text-[10px] opacity-60">(local)</span>
    </button>
    <div class="ml-auto relative w-48">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
           class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400">
        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input type="text" placeholder="Filter…" [(ngModel)]="filterText"
             class="w-full bg-white border border-gray-300 text-gray-900 rounded pl-7 pr-2 py-1 text-xs font-mono
                    dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
    </div>
  </div>

  <!-- Split: list (left) | detail (right) -->
  <div *ngIf="viewMode === 'services'" class="flex-1 flex gap-4 min-h-0">

    <!-- LIST — narrow fixed column so the detail (which owns the log
         viewer) gets the majority of horizontal space. w-72 = 18rem =
         288px, tight against the longest content (24-char service name
         + right-aligned size/time pair truncate on any overflow) so
         the detail pane gets almost all the horizontal room on wide
         browsers. Mike 2026-07-07. -->
    <div class="w-96 flex-shrink-0 border border-gray-200 dark:border-[#434863] rounded overflow-hidden flex flex-col">
      <div class="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-200 dark:border-[#434863] flex justify-between bg-gray-50 dark:bg-app-black-100/40">
        <span>Service ({{ filteredServices().length }})</span>
        <span>Log size · Last activity</span>
      </div>
      <div class="overflow-auto flex-1">
        <button *ngFor="let s of filteredServices()" type="button"
                class="w-full text-left px-3 py-2 text-xs border-b border-gray-100 dark:border-[#434863]
                       hover:bg-gray-50 dark:hover:bg-app-black-100/60 flex justify-between gap-3"
                [class.bg-gray-100]="detail?.name === s.name && detail?.box === s.box"
                [class.dark:bg-app-black-100]="detail?.name === s.name && detail?.box === s.box"
                (click)="openDetail(s)">
          <div class="flex flex-col min-w-0">
            <span class="font-mono text-gray-900 dark:text-white truncate">
              <!-- IT-F2-215: status dot — green online, red errored/stopped,
                   amber launching/stopping, gray unknown (pm2 unreachable). -->
              <span *ngIf="s.status"
                    class="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                    [ngClass]="{
                      'bg-emerald-500': s.status === 'online',
                      'bg-red-500': s.status === 'errored' || s.status === 'stopped',
                      'bg-amber-400': s.status === 'launching' || s.status === 'stopping' || s.status === 'one-launch-status',
                      'bg-gray-400': !['online','errored','stopped','launching','stopping','one-launch-status'].includes(s.status)
                    }"
                    [title]="'pm2 status: ' + s.status"></span>
              <span *ngIf="!s.status" class="inline-block w-2 h-2 rounded-full mr-1 align-middle bg-gray-400"
                    title="no live pm2 data — service in dump but not currently tracked"></span>
              {{ s.name }}
              <!-- systemd badge: makes it obvious in the list which rows
                   are pm2-managed vs system units. Restart etc. are
                   scoped differently for these — see the detail pane. -->
              <span *ngIf="s.exec_mode === 'systemd'"
                    class="ml-1 inline-block px-1 py-[1px] text-[9px] uppercase tracking-wider rounded border border-app-teal-100/50 text-app-teal-100 align-middle">systemd</span>
              <!-- IT-F2-215: unstable-restart flag — surface flapping without opening detail. -->
              <span *ngIf="s.unstable_restarts && s.unstable_restarts > 0"
                    class="ml-1 inline-block px-1 py-[1px] text-[9px] uppercase tracking-wider rounded border border-red-400/60 text-red-400 align-middle"
                    [title]="s.unstable_restarts + ' unstable restarts — service is flapping'">↻{{ s.unstable_restarts }}</span>
            </span>
            <!-- IT-F2-215 (2026-08-10 followup): all runtime metadata on
                 the secondary line — box · port · mode · up · ↻ · CPU% · mem.
                 Previously CPU/mem lived on the right column, which
                 pushed the right block wide + crushed the name column to
                 6-8 chars. Consolidating here + widening the panel from
                 w-72→w-96 gives names their full breathing room again. -->
            <span class="text-[10px] text-gray-500 dark:text-gray-400 truncate">
              {{ s.box }}<span *ngIf="s.port"> · :{{ s.port }}</span><span *ngIf="s.exec_mode"> · {{ s.exec_mode }}</span><span *ngIf="s.uptime_ms !== undefined"> · up {{ formatDuration(s.uptime_ms) }}</span><span *ngIf="s.restart_count !== undefined && s.restart_count > 0"> · ↻{{ s.restart_count }}</span><span *ngIf="s.cpu_percent !== undefined"> · {{ s.cpu_percent | number:'1.0-1' }}%</span><span *ngIf="s.memory_bytes !== undefined"> · {{ formatBytes(s.memory_bytes) }}</span>
            </span>
          </div>
          <div class="text-right flex-shrink-0">
            <div class="font-mono text-gray-700 dark:text-gray-300">{{ formatBytes(s.total_log_size_bytes) }}</div>
            <div class="text-[10px] text-gray-500 dark:text-gray-400">{{ formatTime(s.last_modified) }}</div>
          </div>
        </button>
        <div *ngIf="filteredServices().length === 0" class="text-xs text-gray-500 dark:text-gray-400 px-3 py-6 text-center">
          No services match.
        </div>
      </div>
    </div>

    <!-- DETAIL — takes the remaining width. min-w-0 lets the child pre/
         log tail truncate + break instead of pushing the layout wide. -->
    <div class="flex-1 min-w-0 border border-gray-200 dark:border-[#434863] rounded overflow-hidden flex flex-col">
      <ng-container *ngIf="!detail">
        <div class="text-xs text-gray-500 dark:text-gray-400 px-3 py-6 text-center">
          Pick a service to view settings &amp; logs.
        </div>
      </ng-container>

      <ng-container *ngIf="detail">
        <!-- Detail header + tabs -->
        <div class="px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100/40 flex items-center gap-3">
          <div class="flex-1 min-w-0">
            <div class="font-mono text-sm text-gray-900 dark:text-white truncate">{{ detail.name }}</div>
            <div class="text-[10px] text-gray-500 dark:text-gray-400">
              {{ detail.box }}<span *ngIf="detail.port"> · :{{ detail.port }}</span><span *ngIf="detail.cwd"> · {{ detail.cwd }}</span>
            </div>
          </div>
          <button type="button" class="text-[11px] uppercase tracking-wide px-2 py-1 border rounded"
                  [class.bg-app-teal-100]="detailTab === 'settings'"
                  [class.text-white]="detailTab === 'settings'"
                  [class.border-gray-300]="detailTab !== 'settings'"
                  [class.dark:border-app-black-100]="detailTab !== 'settings'"
                  (click)="detailTab = 'settings'">Settings</button>
          <button type="button" class="text-[11px] uppercase tracking-wide px-2 py-1 border rounded"
                  [class.bg-app-teal-100]="detailTab === 'logs'"
                  [class.text-white]="detailTab === 'logs'"
                  [class.border-gray-300]="detailTab !== 'logs'"
                  [class.dark:border-app-black-100]="detailTab !== 'logs'"
                  (click)="openLogTab()">Logs</button>
          <button type="button" class="text-[11px] text-gray-500 hover:text-gray-900 dark:hover:text-white px-2" (click)="closeDetail()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Settings tab -->
        <ng-container *ngIf="detailTab === 'settings'">
          <div class="overflow-auto flex-1 p-3 text-xs">
            <div class="grid grid-cols-3 gap-2 mb-3">
              <div><div class="uppercase text-[10px] text-gray-500">pm_id</div><div class="font-mono">{{ detail.pm_id ?? '—' }}</div></div>
              <div><div class="uppercase text-[10px] text-gray-500">Mode</div><div class="font-mono">{{ detail.exec_mode || '—' }} × {{ detail.instances ?? 1 }}</div></div>
              <div><div class="uppercase text-[10px] text-gray-500">Port</div><div class="font-mono">{{ detail.port || '—' }}</div></div>
              <div><div class="uppercase text-[10px] text-gray-500">Git HEAD</div><div class="font-mono truncate">{{ detail.git?.head?.slice(0,12) || '—' }}<span *ngIf="detail.git?.branch" class="text-gray-500"> ({{ detail.git?.branch }})</span></div></div>
              <div class="col-span-2"><div class="uppercase text-[10px] text-gray-500">Script</div><div class="font-mono truncate">{{ detail.script || '—' }}</div></div>
            </div>

            <div *ngIf="remoteEntries().length" class="mb-3">
              <div class="uppercase text-[10px] text-gray-500 mb-1">Git Remotes</div>
              <table class="w-full font-mono text-[11px]">
                <tr *ngFor="let r of remoteEntries()" class="border-b border-gray-100 dark:border-[#434863]">
                  <td class="py-1 pr-2 text-gray-600 dark:text-gray-400 align-top w-24">{{ r.key }}</td>
                  <td class="py-1 truncate">{{ r.value }}</td>
                </tr>
              </table>
            </div>

            <div>
              <div class="flex items-center justify-between mb-1">
                <div class="uppercase text-[10px] text-gray-500">Environment ({{ envEntries().length }} keys)</div>
                <div class="text-[10px] text-gray-500">Secret-named values masked</div>
              </div>
              <table class="w-full font-mono text-[11px]">
                <tr *ngFor="let e of envEntries()" class="border-b border-gray-100 dark:border-[#434863] align-top">
                  <td class="py-1 pr-2 text-gray-700 dark:text-gray-300 w-1/3">{{ e.key }}</td>
                  <td class="py-1 break-all">{{ e.value }}</td>
                </tr>
              </table>
            </div>
          </div>
        </ng-container>

        <!-- Logs tab -->
        <ng-container *ngIf="detailTab === 'logs'">
          <div class="flex border-b border-gray-200 dark:border-[#434863]">
            <!-- File list -->
            <div class="w-64 border-r border-gray-200 dark:border-[#434863] overflow-auto" style="max-height: 60vh">
              <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400 px-2 py-1 sticky top-0 bg-gray-50 dark:bg-app-black-100/40 border-b border-gray-200 dark:border-[#434863]">
                Files
              </div>
              <button *ngFor="let f of detail.log_files" type="button"
                      class="w-full text-left px-2 py-1 text-[11px] border-b border-gray-100 dark:border-[#434863]
                             hover:bg-gray-50 dark:hover:bg-app-black-100/60"
                      [class.bg-gray-100]="selectedFile?.file === f.file"
                      [class.dark:bg-app-black-100]="selectedFile?.file === f.file"
                      (click)="openFile(f)">
                <div class="font-mono truncate text-gray-900 dark:text-white">{{ f.file }}</div>
                <div class="flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
                  <span>{{ f.kind }}</span>
                  <span>{{ formatBytes(f.size_bytes) }}</span>
                </div>
              </button>
            </div>

            <!-- Tail viewer -->
            <div class="flex-1 flex flex-col min-w-0">
              <div *ngIf="selectedFile" class="px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100/40 flex items-center gap-2 text-[11px]">
                <span class="font-mono truncate">{{ selectedFile.file }}</span>
                <!-- Search box — live-highlights matches with <mark>
                     across the currently-visible tail page. Doesn't
                     filter lines out, matches Chrome DevTools UX. -->
                <div class="flex-1 min-w-0 max-w-xs relative">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400">
                    <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input type="text" [(ngModel)]="tailSearch" placeholder="Search…"
                         class="w-full bg-white border border-gray-300 text-gray-900 rounded pl-6 pr-2 py-0.5 text-[11px] font-mono
                                dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
                </div>
                <button *ngIf="tailSearch" type="button"
                        class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 border border-gray-300 dark:border-[#434863] rounded"
                        (click)="tailSearch = ''" title="Clear search">
                  <i class="fa-solid fa-xmark"></i>
                </button>
                <span class="text-gray-500">{{ formatBytes(tail?.size ?? selectedFile.size_bytes) }}</span>
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        [disabled]="loadingTail"
                        (click)="refreshTail()"><i class="fa-solid fa-rotate mr-1"></i>Reload</button>
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        [disabled]="!tail?.lines?.length"
                        [class.opacity-40]="!tail?.lines?.length"
                        (click)="copyTail()"><i class="fa-solid" [ngClass]="copiedTail ? 'fa-check text-emerald-500' : 'fa-copy'"></i><span class="ml-1">{{ copiedTail ? 'Copied' : 'Copy' }}</span></button>
                <!-- IT-F2-215: Copy Context — bundles service identity
                     (name/box/pm_id/status/uptime/restarts/cpu/mem) +
                     script+cwd+git HEAD + log file metadata + currently
                     loaded tail into a markdown block Mike pastes to an
                     agent for diagnostics so nothing gets stripped. -->
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-app-teal-100 text-app-teal-100 rounded"
                        [disabled]="!tail?.lines?.length"
                        [class.opacity-40]="!tail?.lines?.length"
                        (click)="copyContext()"
                        title="Copy service metadata + this log's tail as a markdown block, ready to paste into a chat with an agent"><i class="fa-solid" [ngClass]="copiedContext ? 'fa-check text-emerald-500' : 'fa-share-nodes'"></i><span class="ml-1">{{ copiedContext ? 'Copied' : 'Copy Context' }}</span></button>
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        (click)="download(selectedFile)"><i class="fa-solid fa-download mr-1"></i>Download</button>
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded"
                        [disabled]="clearing"
                        (click)="clearFile(selectedFile)"><i class="fa-solid fa-eraser mr-1"></i>Clear</button>
              </div>

              <div class="flex-1 overflow-auto bg-white dark:bg-app-black-100/20" style="max-height: 60vh">
                <!-- Per-line rendering so each log line is its own
                     hover target: subtle highlight + a Copy pill in
                     the top-right corner that appears on hover +
                     copies that entire line (including its wrapped
                     continuation) to the clipboard. Right-click on
                     any part of the line copies directly. Mike
                     2026-07-07. -->
                <div *ngIf="tail" class="text-[11px] font-mono py-2 leading-snug text-gray-800 dark:text-gray-200">
                  <!-- Per-line: robust hover highlight (bg-yellow tint
                       against both light + dark themes), search-term
                       highlighting via [innerHTML]+SafeHtml, and a
                       hover-shown Copy pill in the top-right. Right-
                       click anywhere on the line copies too. -->
                  <div *ngFor="let line of tail.lines; let i = index"
                       class="group relative px-3 whitespace-pre-wrap break-all cursor-default hover:bg-yellow-100 dark:hover:bg-yellow-500/25 hover:text-black dark:hover:text-white"
                       (contextmenu)="onLineContext($event, line, i)">
                    <span [innerHTML]="highlightedLine(line)"></span>
                    <button type="button"
                            class="opacity-0 group-hover:opacity-100 absolute top-0 right-1 text-[9px] px-1.5 py-0.5 border border-gray-400 dark:border-[#434863] bg-white/90 dark:bg-app-black-100/95 text-gray-700 dark:text-gray-200 rounded shadow transition-opacity"
                            (click)="copyLine(line, i)"
                            title="Copy this line">
                      <i class="fa-solid" [ngClass]="copiedLineIdx === i ? 'fa-check text-emerald-500' : 'fa-copy'"></i>
                      <span class="ml-1">{{ copiedLineIdx === i ? 'Copied' : 'Copy' }}</span>
                    </button>
                  </div>
                </div>
                <div *ngIf="loadingTail" class="text-xs text-gray-500 dark:text-gray-400 px-3 py-6 text-center">Loading…</div>
                <div *ngIf="!tail && !loadingTail" class="text-xs text-gray-500 dark:text-gray-400 px-3 py-6 text-center">Pick a file.</div>
              </div>

              <div *ngIf="selectedFile" class="px-3 py-2 border-t border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100/40 flex items-center gap-2 text-[11px]">
                <!-- Oldest = jump to BOF (first N lines). Read-only view;
                     Older is disabled at BOF, Newer is disabled outside
                     a page walk — operator uses Newest to return. -->
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        [disabled]="loadingTail"
                        [class.opacity-40]="loadingTail"
                        (click)="jumpOldest()"><i class="fa-solid fa-angles-up mr-1"></i>Oldest</button>
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        [disabled]="!canPageOlder() || loadingTail"
                        [class.opacity-40]="!canPageOlder() || loadingTail"
                        (click)="pageOlder()"><i class="fa-solid fa-arrow-up mr-1"></i>Older</button>
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        [disabled]="!canPageNewer() || loadingTail"
                        [class.opacity-40]="!canPageNewer() || loadingTail"
                        (click)="pageNewer()"><i class="fa-solid fa-arrow-down mr-1"></i>Newer</button>
                <!-- Newest = jump to EOF (live tail). Semantically like
                     Reload from the active tail — the intent-communicating
                     variant that returns from an Older- or Oldest-walked view. -->
                <button type="button" class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                        [disabled]="loadingTail"
                        [class.opacity-40]="loadingTail"
                        (click)="jumpNewest()"><i class="fa-solid fa-angles-down mr-1"></i>Newest</button>
                <span class="ml-auto text-gray-500">{{ tail?.lines?.length || 0 }} lines · paging {{ pageStack.length || 0 }}</span>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </div>
  </div>

  <!-- Crons view — fleet-wide node-cron registry, one row per (service,
       box, cron_id). Same box filter + search input above narrow both
       modes. Backend aggregates F2-ADMIN.CronRegistry + latest CronRuns
       and computes next_run via cron-parser. Mike 2026-07-04. -->
  <div *ngIf="viewMode === 'crons'" class="flex-1 flex flex-col border border-gray-200 dark:border-[#434863] rounded overflow-hidden min-h-0">
    <div class="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100/40 flex items-center gap-3">
      <span>Cron ({{ filteredCrons().length }})</span>
      <div class="ml-auto relative w-64">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400">
          <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input type="text" placeholder="Filter cron / service / description…" [(ngModel)]="cronsFilterText"
               class="w-full bg-white border border-gray-300 text-gray-900 rounded pl-7 pr-2 py-1 text-xs font-mono
                      dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
      </div>
    </div>
    <div class="overflow-auto flex-1">
      <table class="w-full text-xs">
        <thead class="bg-gray-50 dark:bg-app-black-100/40 text-gray-500 dark:text-gray-400 sticky top-0">
          <tr>
            <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Service · Cron</th>
            <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Box</th>
            <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Schedule</th>
            <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Last run</th>
            <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Next run</th>
            <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Duration</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-[#2b2f47]">
          <tr *ngIf="filteredCrons().length === 0">
            <td colspan="6" class="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
              No crons registered yet. Services register themselves at boot — first row appears once
              at least one instrumented service has been restarted with the cron-runner in place.
            </td>
          </tr>
          <tr *ngFor="let r of filteredCrons()"
              class="hover:bg-gray-50 dark:hover:bg-app-blue-100/10">
            <td class="px-3 py-1.5">
              <div class="font-mono text-gray-900 dark:text-white">{{ r.service }}</div>
              <div class="text-[10px] text-gray-500 dark:text-gray-400">
                <span class="font-mono">{{ r.cron_id }}</span>
                <span *ngIf="r.description"> · {{ r.description }}</span>
              </div>
            </td>
            <td class="px-3 py-1.5 text-gray-700 dark:text-gray-300">{{ r.box }}</td>
            <td class="px-3 py-1.5">
              <div class="text-gray-700 dark:text-gray-300">{{ humanFreq(r.cron_expr, r.timezone) }}</div>
              <div class="text-[10px] font-mono text-gray-500 dark:text-gray-500">{{ r.cron_expr }}</div>
            </td>
            <td class="px-3 py-1.5">
              <ng-container *ngIf="r.last_run; else neverRun">
                <div class="flex items-center gap-1">
                  <span *ngIf="lastRunStatus(r) === 'ok'"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                               bg-emerald-500/15 text-emerald-400 text-[10px] uppercase tracking-wide">
                    <i class="fa-solid fa-circle text-[6px]"></i>OK
                  </span>
                  <span *ngIf="lastRunStatus(r) === 'error'"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                               bg-red-500/15 text-red-400 text-[10px] uppercase tracking-wide"
                        [title]="r.last_run?.err || ''">
                    <i class="fa-solid fa-circle text-[6px]"></i>ERROR
                  </span>
                  <span *ngIf="lastRunStatus(r) === 'running'"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                               bg-amber-500/15 text-amber-400 text-[10px] uppercase tracking-wide">
                    <i class="fa-solid fa-circle text-[6px]"></i>RUNNING
                  </span>
                </div>
                <div class="text-[10px] text-gray-500 dark:text-gray-400">{{ formatTime(r.last_run.started_at) }}</div>
              </ng-container>
              <ng-template #neverRun>
                <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                             bg-gray-500/15 text-gray-400 text-[10px] uppercase tracking-wide">
                  <i class="fa-regular fa-circle text-[8px]"></i>never
                </span>
              </ng-template>
            </td>
            <td class="px-3 py-1.5 text-[10px] text-gray-500 dark:text-gray-400">{{ formatTime(r.next_run || undefined) || '—' }}</td>
            <td class="px-3 py-1.5 text-[11px] font-mono text-gray-700 dark:text-gray-300">
              {{ formatDuration(r.last_run?.duration_ms) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Commerce Connections — independent of the pm2 split above.
       Scans Customers and lists each commerce integration (WooCommerce,
       Kartra, …) with its default scanner + product-map size. Click
       Open to deep-link into customer-directory's edit modal with the
       Commerce Settings section already expanded. -->
  <div class="mt-6 border border-gray-200 dark:border-[#434863] rounded overflow-hidden">
    <div class="px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100/40 flex items-center gap-3">
      <span class="text-[11px] uppercase tracking-wide text-gray-700 dark:text-gray-300 font-semibold">Commerce Connections</span>
      <span class="text-[11px] text-gray-500 dark:text-gray-400">
        {{ commerceEnabledCount() }} enabled · {{ commerceRows.length }} configured
      </span>
      <label class="ml-auto text-[11px] text-gray-600 dark:text-gray-400 flex items-center gap-1 cursor-pointer select-none">
        <input type="checkbox" [(ngModel)]="commerceShowDisabled"
               class="accent-app-teal-100" />
        Show disabled
      </label>
      <div class="relative w-48">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400">
          <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input type="text" placeholder="Filter…" [(ngModel)]="commerceFilter"
               class="w-full bg-white border border-gray-300 text-gray-900 rounded pl-7 pr-2 py-1 text-xs font-mono
                      dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
      </div>
      <button type="button"
              class="text-[11px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-1
                     text-gray-700 dark:text-gray-300 hover:text-app-teal-100 hover:border-app-teal-100 transition"
              [disabled]="commerceLoading"
              (click)="loadCommerceConnections()">
        <i class="fa-solid fa-rotate mr-1" [class.fa-spin]="commerceLoading"></i>Refresh
      </button>
    </div>
    <table class="w-full text-xs">
      <thead class="bg-gray-50 dark:bg-app-black-100/40 text-gray-500 dark:text-gray-400">
        <tr>
          <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Customer</th>
          <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Integration</th>
          <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Status</th>
          <th class="px-3 py-1.5 text-left font-medium uppercase tracking-wide">Default Scanner</th>
          <th class="px-3 py-1.5 text-right font-medium uppercase tracking-wide">Products</th>
          <th class="px-3 py-1.5 text-right font-medium uppercase tracking-wide"></th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100 dark:divide-[#2b2f47]">
        <tr *ngIf="!commerceLoading && filteredCommerceRows().length === 0">
          <td colspan="6" class="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
            No commerce connections.
          </td>
        </tr>
        <tr *ngFor="let row of filteredCommerceRows()"
            class="hover:bg-gray-50 dark:hover:bg-app-blue-100/10"
            [class.opacity-50]="!row.enabled">
          <td class="px-3 py-1.5 text-gray-900 dark:text-white">
            {{ row.customer_name }}
            <span class="ml-1 text-[10px] text-gray-500 dark:text-gray-500 font-mono">{{ row.customer_id }}</span>
          </td>
          <td class="px-3 py-1.5">{{ row.integration_label }}</td>
          <td class="px-3 py-1.5">
            <span *ngIf="row.enabled"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                         bg-emerald-500/15 text-emerald-400 text-[10px] uppercase tracking-wide">
              <i class="fa-solid fa-circle text-[6px]"></i>Enabled
            </span>
            <span *ngIf="!row.enabled"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                         bg-gray-500/15 text-gray-400 text-[10px] uppercase tracking-wide">
              <i class="fa-regular fa-circle text-[8px]"></i>Disabled
            </span>
          </td>
          <td class="px-3 py-1.5 font-mono text-[11px] text-gray-700 dark:text-gray-300">
            {{ row.default_scanner || '—' }}
          </td>
          <td class="px-3 py-1.5 text-right font-mono text-gray-700 dark:text-gray-300">
            {{ row.product_count }}
          </td>
          <td class="px-3 py-1.5 text-right">
            <button type="button"
                    (click)="openCommerceSettings(row)"
                    class="text-[10px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5
                           text-gray-700 dark:text-gray-300 hover:text-app-teal-100 hover:border-app-teal-100 transition">
              Open<i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[9px]"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

</div>
